/**
 * Toast queue (NIT-013). One global store; `Toast.tsx` renders the
 * viewport. Toasts auto-dismiss after `defaultDurationMs` (2.5 s).
 *
 * Use cases (per the parity-gaps fix_sketch):
 *   - Offline session Add        → "Added to queue"
 *   - Profile install            → "Profile #<id> installed"
 *   - Settings displayName save  → "Display name saved"
 *   - Settings Clear stale       → "<N> transactions marked orphaned"
 *   - Snapshots Export / Import  → respective summaries
 */
import { create } from 'zustand';

export type ToastKind = 'success' | 'info' | 'error';

export interface Toast {
  readonly id: string;
  readonly kind: ToastKind;
  readonly text: string;
  readonly createdAt: number;
}

export interface ToastStoreState {
  readonly toasts: readonly Toast[];
  readonly defaultDurationMs: number;
}

export interface ToastStoreActions {
  push(kind: ToastKind, text: string): string;
  dismiss(id: string): void;
  clear(): void;
}

export type ToastStore = ToastStoreState & ToastStoreActions;

let counter = 0;
function nextId(): string {
  counter += 1;
  return `toast-${Date.now()}-${counter}`;
}

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  defaultDurationMs: 2_500,
  push(kind, text): string {
    const id = nextId();
    const toast: Toast = { id, kind, text, createdAt: Date.now() };
    set((s) => ({ toasts: [...s.toasts, toast] }));
    return id;
  },
  dismiss(id): void {
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  },
  clear(): void {
    set({ toasts: [] });
  },
}));

/** Convenience wrappers used by callers across the UI. */
export function toast(text: string): void {
  useToastStore.getState().push('success', text);
}
export function toastError(text: string): void {
  useToastStore.getState().push('error', text);
}
export function toastInfo(text: string): void {
  useToastStore.getState().push('info', text);
}
